// What every page has around what it draws: the header's theme toggle and
// search box, game art's fallback, and a game page's embed Copy button.
// Set up as it's imported (app.ts imports it once).

import { closeUpdateMenu } from "./chart-draw.ts";
import { app, element, eventTarget } from "./dom.ts";
import { saveValue } from "./prefs.ts";
import { navigate } from "./router.ts";
import { searchHref, searchQuery } from "./routes.ts";

// ---- theme ----

// The header's toggle: light or dark over the system's setting. Picking the
// theme the system already has forgets the choice, so the page follows the
// system again. index.html's head applies a saved choice before first paint.
const themeToggle = element(".theme-toggle", HTMLButtonElement);
const systemDark = window.matchMedia("(prefers-color-scheme: dark)");
const themeColors = { light: "#f5f6f8", dark: "#0e1116" } as const;

type Theme = keyof typeof themeColors;

function currentTheme(): Theme {
  const picked = document.documentElement.dataset.theme;
  return picked === "dark" || picked === "light" ? picked : systemDark.matches ? "dark" : "light";
}

function showTheme(): void {
  const theme = currentTheme();
  const next = theme === "dark" ? "light" : "dark";
  // An attribute: SVG elements have no .hidden.
  element(".theme-icon-dark", SVGElement, themeToggle).toggleAttribute("hidden", next !== "dark");
  element(".theme-icon-light", SVGElement, themeToggle).toggleAttribute("hidden", next !== "light");
  themeToggle.setAttribute("aria-label", `Switch to ${next} theme`);
  themeToggle.title = `Switch to ${next} theme`;
  // The browser's own bar: both metas, as either may be the one that applies.
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = themeColors[theme];
  }
}

themeToggle.addEventListener("click", () => {
  const next = currentTheme() === "dark" ? "light" : "dark";
  const system = systemDark.matches ? "dark" : "light";
  saveValue("theme", next === system ? null : next);
  // The page, and an embed card's preview on it (embed.html follows the
  // page's theme as it loads, and this one from then on).
  const roots = [document, ...[...document.querySelectorAll<HTMLIFrameElement>(".embed-preview")].map((f) => f.contentDocument)];
  for (const root of roots) {
    if (!root) continue;
    if (next === system) delete root.documentElement.dataset.theme;
    else root.documentElement.dataset.theme = next;
  }
  showTheme();
});

systemDark.addEventListener("change", showTheme);
showTheme();
themeToggle.hidden = false;

// ---- search ----

// The header's search box, on every page but the search page (see
// showSearchBox). Enter opens the search page for the name, /search?q=…; an
// empty box does nothing (its "Advanced search" link opens the page as it
// is). Without app.js the form itself goes there, and the Worker writes the
// results in.
const searchForm = element(".search", HTMLFormElement);
const searchInput = element("input", HTMLInputElement, searchForm);

// Shows the header's search box, emptied, or hides it: the search page has
// its own box.
export function showSearchBox(shown: boolean): void {
  searchForm.hidden = !shown;
  searchInput.value = "";
}

// "/" puts the cursor in the search box (on the search page, its own), unless
// typing somewhere already or with a modifier held.
document.addEventListener("keydown", (event) => {
  if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey) return;
  const target = event.target;
  if (target instanceof HTMLElement && (target.isContentEditable || target.closest("input, textarea, select"))) {
    return;
  }

  const input = app.querySelector<HTMLInputElement>("form.search-form input[name=q]") ?? searchInput;
  if (!input.offsetParent) return;

  // An open update menu goes, or Escape there would take the focus back.
  closeUpdateMenu();
  event.preventDefault();
  input.focus();
  input.select();
});

searchForm.addEventListener("submit", (event) => {
  event.preventDefault();

  const { q } = searchQuery(new URLSearchParams({ q: searchInput.value }));
  if (q) navigate(searchHref({ q, tags: {} }));
});

// ---- art and embeds ----

// A game's art (markup.ts) is an image in a box labelled with its initials:
// if the image fails, show them instead.
// (error events don't bubble, so listen in the capture phase.)
document.addEventListener(
  "error",
  (event) => {
    const box = event.target instanceof HTMLImageElement && event.target.closest<HTMLElement>(".art");
    if (box) box.textContent = box.dataset.label ?? "";
  },
  true
);

// A game page's Copy code button (game.ts's embedPanel): the iframe tag to
// the clipboard, or, where the browser won't allow it, selected to copy by
// hand.
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

app.addEventListener("click", (event) => {
  const button = eventTarget<HTMLButtonElement>(event, ".embed-copy");
  const code = button?.parentElement?.querySelector("textarea");
  if (!button || !code) return;

  // No clipboard over plain http, or in some webviews.
  if (!navigator.clipboard) {
    code.select();
    return;
  }
  navigator.clipboard.writeText(code.value).then(
    () => {
      button.textContent = "Copied";
      // A second copy shows its own two seconds.
      clearTimeout(copiedTimer);
      copiedTimer = setTimeout(() => (button.textContent = "Copy code"), 2000);
    },
    () => code.select()
  );
});
